import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pause, Play, Plus, Repeat, Zap } from "lucide-react";
import { api, errMsg, inr } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { EmptyState, PageHeader, StatusBadge, TableSkeleton, fmtDate } from "../components/erp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Trash2 } from "lucide-react";
import AddressFields, { EMPTY_ADDRESS, addressFromParty, addressText } from "../components/AddressFields";

const FREQS = ["weekly", "monthly", "quarterly", "yearly"];
const newLine = () => ({ item_id: null, name: "", hsn: "", unit: "Nos", qty: 1, rate: 0, discount_pct: 0, tax_rate: 18 });

export default function Recurring() {
  const { activeId } = useCompany();
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [partyId, setPartyId] = useState("");
  const [frequency, setFrequency] = useState("monthly");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [billingAddress, setBillingAddress] = useState(EMPTY_ADDRESS);
  const [shippingAddress, setShippingAddress] = useState(EMPTY_ADDRESS);
  const [lines, setLines] = useState([newLine()]);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = () => api.get("/recurring").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (activeId) { setData(null); load(); } }, [activeId]); // eslint-disable-line

  useEffect(() => {
    if (!dialog) return;
    api.get("/parties?limit=200").then((r) => setParties(r.data.items.filter((p) => !p.archived))).catch(() => {});
    api.get("/items?limit=200").then((r) => setItems(r.data.items)).catch(() => {});
  }, [dialog]);

  useEffect(() => {
    const p = parties.find((x) => x.id === partyId);
    if (p) { const b = addressFromParty(p, "billing"); const sh = addressFromParty(p, "shipping"); setBillingAddress(b); setShippingAddress(sh.line1 ? sh : b); }
  }, [partyId, parties]);

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const pickItem = (i, itemId) => {
    if (itemId === "custom") { setLine(i, "item_id", null); return; }
    const it = items.find((x) => x.id === itemId);
    if (it) setLines((ls) => ls.map((l, j) => j === i ? { ...l, item_id: it.id, name: it.name, hsn: it.hsn, unit: it.unit, rate: it.sale_price, tax_rate: it.tax_rate } : l));
  };

  const save = async () => {
    if (!partyId) { toast.error("Select a customer"); return; }
    const valid = lines.filter((l) => l.name.trim() && +l.qty > 0);
    if (!valid.length) { toast.error("Add at least one item"); return; }
    setBusy(true);
    try {
      await api.post("/recurring", { party_id: partyId, frequency, start_date: startDate, notes, billing_address_details: billingAddress, billing_address: addressText(billingAddress), shipping_address_details: shippingAddress, shipping_address: addressText(shippingAddress),
        items: valid.map((l) => ({ ...l, qty: +l.qty, rate: +l.rate, discount_pct: +l.discount_pct, tax_rate: +l.tax_rate })) });
      toast.success("Recurring invoice schedule created");
      setDialog(false); setPartyId(""); setNotes(""); setBillingAddress(EMPTY_ADDRESS); setShippingAddress(EMPTY_ADDRESS); setLines([newLine()]);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const run = async (rec) => {
    try {
      const { data: r } = await api.post(`/recurring/${rec.id}/run`);
      toast.success(`Invoice ${r.invoice_no} generated`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const runDue = async () => {
    try {
      const { data: r } = await api.post("/recurring/run-due");
      toast.success(r.generated.length ? `Generated: ${r.generated.join(", ")}` : "Nothing due right now");
      if (r.errors.length) toast.warning(r.errors.join("; "));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggle = async (rec) => {
    try {
      const { data: r } = await api.post(`/recurring/${rec.id}/toggle`);
      toast.success(`Schedule ${r.status}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="recurring-page">
      <PageHeader title="Recurring Invoices" testId="recurring-title" sub="Automated repeat billing — generates posted GST invoices on schedule">
        <Button variant="outline" data-testid="run-due-btn" onClick={runDue}><Zap className="mr-1.5 h-4 w-4" /> Run Due Now</Button>
        <Button data-testid="new-recurring-btn" onClick={() => setDialog(true)}
          className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><Plus className="mr-1.5 h-4 w-4" /> New Schedule</Button>
      </PageHeader>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        {!data ? <TableSkeleton /> : data.items.length === 0 ? (
          <EmptyState testId="recurring-empty" title="No recurring schedules"
            body="Set up repeat billing for retainers, rent or AMC contracts." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recurring-table">
              <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="p-3">Schedule</th><th className="p-3">Customer</th><th className="p-3">Frequency</th>
                <th className="p-3">Next Invoice</th><th className="p-3">Last Generated</th><th className="p-3 text-right">Count</th>
                <th className="p-3">Status</th><th className="p-3"></th></tr></thead>
              <tbody>
                {data.items.map((r) => (
                  <tr key={r.id} data-testid={`recurring-row-${r.doc_no}`} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                    <td className="p-3 font-mono text-xs font-semibold">{r.doc_no}</td>
                    <td className="p-3 font-medium max-w-[200px] truncate">{r.party_name}</td>
                    <td className="p-3 capitalize text-slate-500">{r.frequency}</td>
                    <td className="p-3 font-mono text-xs">{r.next_date}</td>
                    <td className="p-3">{fmtDate(r.last_generated)}</td>
                    <td className="p-3 text-right font-mono">{r.generated_count}</td>
                    <td className="p-3">
                      <span data-testid={`recurring-status-${r.doc_no}`} className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium capitalize ${
                        r.status === "active"
                          ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-400"
                          : "bg-slate-100 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300"}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1.5 justify-end">
                        {r.status === "active" && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`recurring-run-${r.doc_no}`} onClick={() => run(r)}>
                            <Play className="mr-1 h-3 w-3" /> Run Now
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-7" data-testid={`recurring-toggle-${r.doc_no}`} onClick={() => toggle(r)}>
                          {r.status === "active" ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900" data-testid="recurring-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Repeat className="h-5 w-5 text-amber-500" /> New Recurring Schedule</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-3 sm:col-span-1"><Label>Customer *</Label>
              <Select value={partyId} onValueChange={setPartyId}>
                <SelectTrigger data-testid="recurring-party" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Frequency</Label>
              <Select value={frequency} onValueChange={setFrequency}>
                <SelectTrigger data-testid="recurring-frequency" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{FREQS.map((f) => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Starts / Next On</Label><Input data-testid="recurring-start" type="date" className="mt-1.5" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-1 gap-3 mt-4">
            <AddressFields title="Billing Address" value={billingAddress} onChange={setBillingAddress} icon="billing" copyLabel="Use Customer Address" onCopy={() => { const p = parties.find((x) => x.id === partyId); if (p) setBillingAddress(addressFromParty(p, "billing")); }} />
            <AddressFields title="Shipping Address" value={shippingAddress} onChange={setShippingAddress} icon="shipping" copyLabel="Use Billing Address" onCopy={() => setShippingAddress({ ...billingAddress })} />
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="font-semibold">Items per invoice</Label>
              <Button size="sm" variant="outline" data-testid="recurring-add-line" onClick={() => setLines((ls) => [...ls, newLine()])}><Plus className="mr-1 h-3.5 w-3.5" /> Line</Button>
            </div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start">
                  <div className="col-span-6">
                    <Select value={l.item_id || (l.name ? "custom" : "")} onValueChange={(v) => pickItem(i, v)}>
                      <SelectTrigger data-testid={`recurring-item-${i}`} className="h-9"><SelectValue placeholder="Select item" /></SelectTrigger>
                      <SelectContent>
                        {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}
                        <SelectItem value="custom">— Custom line —</SelectItem>
                      </SelectContent>
                    </Select>
                    {!l.item_id && <Input className="mt-1 h-8 text-xs" placeholder="Description" value={l.name} onChange={(e) => setLine(i, "name", e.target.value)} />}
                  </div>
                  <Input className="col-span-2 h-9" type="number" min="0" placeholder="Qty" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} />
                  <Input className="col-span-2 h-9" type="number" min="0" placeholder="Rate ₹" data-testid={`recurring-rate-${i}`} value={l.rate} onChange={(e) => setLine(i, "rate", e.target.value)} />
                  <div className="col-span-2 flex items-center gap-1">
                    <Select value={String(l.tax_rate)} onValueChange={(v) => setLine(i, "tax_rate", +v)}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{[0, 5, 12, 18, 28].map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-400 hover:text-red-500" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} disabled={lines.length === 1}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-3"><Label>Notes</Label><Textarea rows={2} className="mt-1.5" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button data-testid="recurring-save-btn" onClick={save} disabled={busy} className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">{busy ? "Saving..." : "Create Schedule"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
